import React from 'react'
import { useController, useFormContext } from 'react-hook-form'
import styled from 'styled-components'

const CheckboxLabel = styled.label<{ $isSelected: boolean }>`
  display: block;
  padding: var(--grid-16);
  border: 2px solid
    ${({ $isSelected }) => ($isSelected ? 'var(--colour-text-primary)' : 'var(--colour-surface-cream-subtle)')};
  border-radius: var(--radius-12);
  background-color: var(--colour-surface-cream-subtle);
  cursor: pointer;
  transition: border-color 0.15s ease;

  /* Only apply hover styling on devices that actually have a pointing
     device — on touch screens the hover state sticks after a tap and
     makes the just-tapped option look like it's still highlighted. */
  @media (hover: hover) {
    &:hover {
      border-color: var(--colour-text-secondary);
    }
  }

  &:has(input:focus-visible) {
    border-color: var(--colour-border-focused);
    outline: 2px solid var(--colour-border-focused);
    outline-offset: 2px;
  }
`

const CheckboxWrapper = styled.div`
  /* Contains HiddenInput. Without a positioned ancestor it resolves against the
     initial containing block, escaping ScreenShell's scroll region and adding
     its own height to the document (WEB-895). */
  position: relative;
`

const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
`

export interface CheckboxProps extends React.ComponentProps<'input'> {
  label: string
  name: string // needed for useController
  value: string
  // When true, selecting this option clears all others (and selecting any other
  // option clears this one) — e.g. "No, I eat everything" vs specific diets.
  exclusive?: boolean
  // The values of the sibling options that are exclusive, so selecting a normal
  // option can clear them.
  exclusiveValues?: string[]
  // Values of the other options in this option's swap group — selecting this
  // option clears them (e.g. Vegetarian / Vegan / Pescatarian).
  groupValues?: string[]
}

export const Checkbox: React.FC<CheckboxProps> = ({ ...props }) => {
  const { field } = useController({ name: props.name })
  const { setValue } = useFormContext()

  const handleChange = () => {
    const set = new Set(Array.isArray(field.value) ? field.value : [])
    // 'none' stays supported as an implicitly-exclusive value for back-compat.
    const isExclusive = props.exclusive || props.value === 'none'
    const exclusiveValues = props.exclusiveValues ?? ['none']

    if (set.has(props.value)) {
      set.delete(props.value)
    } else if (isExclusive) {
      set.clear()
      set.add(props.value)
    } else {
      exclusiveValues.forEach((value) => set.delete(value))
      props.groupValues?.forEach((value) => set.delete(value))
      set.add(props.value)
    }

    setValue(props.name, Array.from(set), { shouldDirty: true, shouldValidate: true })
  }

  const isSelected = field.value?.includes(props.value)

  return (
    <CheckboxWrapper data-testid={`${props.name}-${props.value}-div`}>
      <HiddenInput
        type="checkbox"
        id={`${props.name}-${props.value}`}
        name={field.name}
        ref={field.ref}
        onBlur={field.onBlur}
        value={props.value}
        checked={isSelected}
        onChange={handleChange}
        data-testid={`${props.name}-${props.value}-input`}
      />
      <CheckboxLabel
        $isSelected={isSelected}
        htmlFor={`${props.name}-${props.value}`}
        data-testid={`${props.name}-${props.value}-label`}
      >
        {props.label}
      </CheckboxLabel>
    </CheckboxWrapper>
  )
}
